import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CenterFocusStrongOutlinedIcon from '@mui/icons-material/CenterFocusStrongOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CropSquareOutlinedIcon from '@mui/icons-material/CropSquareOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import ViewInArOutlinedIcon from '@mui/icons-material/ViewInArOutlined';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import {
  Box,
  ButtonBase,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { CollapsibleSection } from 'components/atoms/CollapsibleSection';
import Loader from 'components/Loader';
import React, { lazy, Suspense, useRef, useState } from 'react';
import { formatPeakDetectionSpacingDistanceLabel, PeakDetectionSpacingDistanceGroup } from './helpers';
import { buildSpotDetectionMeshModel, SpotMeshOutcome } from './spotDetectionIllustrations';
import type { SpotDetectionMeshHandle, SpotDetectionMeshView } from './SpotDetectionMeshCanvas';
import { MultiplexMogPeakDetectionChannelProfile } from './types';

const MeshCanvas = lazy(() => import('./SpotDetectionMeshCanvas'));
const number = (value: number) => String(Number(value.toFixed(2)));
const formatContrast = (signal: number, threshold: number) => {
  const rounded = Number(signal.toPrecision(3));
  return String(Math.sign(rounded - threshold) === Math.sign(signal - threshold) ? rounded : signal);
};
const names: Record<SpotMeshOutcome, string> = {
  eligible: 'Kept in example',
  signal: 'Too faint',
  distance: 'Too close',
  small: 'Too small',
  large: 'Too large',
};

interface Props {
  controls?: React.ReactNode;
  profile: MultiplexMogPeakDetectionChannelProfile;
  panelName: string;
  distanceGroups?: PeakDetectionSpacingDistanceGroup[];
  unknownResolutionCount?: number;
  bitDepths?: Array<8 | 16 | null | undefined>;
}

export const SpotDetectionMesh = ({
  controls,
  profile,
  panelName,
  distanceGroups = [],
  unknownResolutionCount = 0,
  bitDepths = [],
}: Props) => {
  const [selected, setSelected] = useState('E');
  const [chosenDepth, setChosenDepth] = useState<8 | 16 | null>(null);
  const [viewMode, setViewMode] = useState<SpotDetectionMeshView>('perspective');
  const canvasRef = useRef<SpotDetectionMeshHandle>(null);
  const depths = ([8, 16] as const).filter((depth) => bitDepths.includes(depth));
  const bitDepth = chosenDepth != null && depths.includes(chosenDepth) ? chosenDepth : depths[0] ?? null;
  const resolution = [...distanceGroups].sort((first, second) => first.umpp - second.umpp)[0];
  const valid =
    Object.values(profile).every((value) => value == null || Number.isFinite(value)) &&
    Number.isInteger(profile.minDistancePx) &&
    profile.minDistancePx >= 1 &&
    profile.minContrast >= 0 &&
    profile.minContrast <= 1 &&
    profile.clipMinFraction >= 0 &&
    profile.clipMinFraction < 1 &&
    (profile.minRadiusUm == null || profile.minRadiusUm >= 0) &&
    (profile.maxRadiusUm == null || profile.maxRadiusUm >= 0) &&
    (profile.minRadiusUm == null || profile.maxRadiusUm == null || profile.minRadiusUm <= profile.maxRadiusUm);
  const model = buildSpotDetectionMeshModel(profile, resolution?.umpp, bitDepth);
  const probe = model.spots.find((spot) => spot.label === selected)!;
  const canRender = typeof WebGLRenderingContext !== 'undefined' || typeof WebGL2RenderingContext !== 'undefined';
  const outcomeColor =
    probe.outcome === 'eligible' ? 'success.main' : probe.outcome === 'distance' ? 'warning.main' : 'error.main';
  return (
    <Box
      component="figure"
      aria-label={`Spot detection mesh for ${panelName}`}
      sx={{
        m: 0,
        display: 'grid',
        gridTemplateColumns: '336px minmax(0, 1fr)',
        minWidth: 0,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Box
        component="aside"
        data-testid="spot-mesh-sidebar"
        sx={{
          p: 2,
          borderRight: 2,
          borderColor: 'divider',
          bgcolor: (theme) => (theme.palette.mode === 'light' ? 'common.white' : 'background.paper'),
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          Detection settings
        </Typography>
        {controls}
      </Box>
      <Box
        data-testid="spot-setting-visualization"
        sx={{ minWidth: 0, bgcolor: (theme) => (theme.palette.mode === 'light' ? 'grey.100' : 'action.hover') }}
      >
        <Box sx={{ px: 2, pt: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="subtitle2">Simulation</Typography>
            <Stack direction="row" spacing={0.5}>
              {depths.length > 1 && (
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  color="secondary"
                  value={bitDepth}
                  aria-label={`${panelName}: Example bit depth`}
                  onChange={(_, value: 8 | 16 | null) => {
                    if (value != null) setChosenDepth(value);
                  }}
                >
                  {depths.map((depth) => (
                    <ToggleButton key={depth} value={depth}>
                      {depth}-bit example
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              )}
              <Tooltip title="3D view">
                <IconButton
                  size="small"
                  color={viewMode === 'perspective' ? 'secondary' : 'inherit'}
                  aria-label="3D view"
                  aria-pressed={viewMode === 'perspective'}
                  onClick={() => setViewMode('perspective')}
                >
                  <ViewInArOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Top view">
                <IconButton
                  size="small"
                  color={viewMode === 'top' ? 'secondary' : 'inherit'}
                  aria-label="Top view"
                  aria-pressed={viewMode === 'top'}
                  onClick={() => setViewMode('top')}
                >
                  <CropSquareOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Side view">
                <IconButton
                  size="small"
                  color={viewMode === 'side' ? 'secondary' : 'inherit'}
                  aria-label="Side view"
                  aria-pressed={viewMode === 'side'}
                  onClick={() => setViewMode('side')}
                >
                  <ViewSidebarOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Center view">
                <IconButton
                  size="small"
                  color="inherit"
                  aria-label="Center view"
                  onClick={() => canvasRef.current?.centerView()}
                >
                  <CenterFocusStrongOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reset view">
                <IconButton
                  size="small"
                  color="inherit"
                  aria-label="Reset view"
                  onClick={() => {
                    setViewMode('perspective');
                    canvasRef.current?.resetView();
                  }}
                >
                  <RestartAltOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          <Typography variant="caption" color="text.secondary" data-testid="spot-intensity-context">
            {bitDepth == null
              ? 'Bit depth unavailable. Raw example intensities are not shown.'
              : `${bitDepth}-bit example; reference peak ${model.referenceMax}. Not measured slide values.`}
          </Typography>
        </Box>
        {!valid ? (
          <Typography variant="body2" sx={{ p: 2 }} color="text.secondary">
            Enter valid clipping, contrast, spacing and radius values to show the example.
          </Typography>
        ) : (
          <>
            {canRender ? (
              <Suspense fallback={<Loader inline message="Loading signal mesh" />}>
                <MeshCanvas
                  key={viewMode}
                  ref={canvasRef}
                  model={model}
                  profile={profile}
                  selectedProbe={selected}
                  onSelectProbe={setSelected}
                  viewMode={viewMode}
                />
              </Suspense>
            ) : (
              <Box sx={{ height: 240, display: 'grid', placeItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  3D rendering unavailable. Probe findings remain available below.
                </Typography>
              </Box>
            )}
            <Stack
              component="section"
              aria-label={`Example results for ${panelName}`}
              data-testid="spot-mesh-results"
              spacing={1.5}
              sx={{ p: 2, borderTop: 2, borderColor: 'divider' }}
            >
              <Typography variant="subtitle2">Example results</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '240px minmax(0, 1fr)', gap: 2 }}>
                <Box
                  data-testid="spot-mesh-findings"
                  sx={{ minWidth: 0, pr: 2, borderRight: 2, borderColor: 'divider' }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Probe findings
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                    {(['eligible', 'signal', 'distance', 'small', 'large'] as const).map((outcome) => {
                      const count = model.spots.filter((spot) => spot.outcome === outcome).length;
                      return count ? (
                        <Typography key={outcome} variant="caption" color="text.secondary">
                          {count} {outcome === 'eligible' ? 'kept' : names[outcome].toLowerCase()}
                        </Typography>
                      ) : null;
                    })}
                  </Stack>
                  {model.spots.map((spot) => (
                    <ButtonBase
                      key={spot.label}
                      onClick={() => setSelected(spot.label)}
                      aria-label={`Select probe ${spot.label}`}
                      aria-pressed={selected === spot.label}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '18px 18px minmax(0, 1fr) auto',
                        gap: 1,
                        width: '100%',
                        p: 0.75,
                        borderRadius: 0.5,
                        textAlign: 'left',
                        bgcolor: selected === spot.label ? 'action.selected' : 'transparent',
                        border: 1,
                        borderColor: selected === spot.label ? 'secondary.main' : 'transparent',
                      }}
                    >
                      {spot.outcome === 'eligible' ? (
                        <CheckCircleOutlineIcon sx={{ fontSize: 18, color: 'success.main' }} />
                      ) : (
                        <CancelOutlinedIcon sx={{ fontSize: 18, color: 'error.main' }} />
                      )}
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {spot.label}
                      </Typography>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          component="div"
                          sx={{ fontWeight: 600, color: spot.outcome === 'eligible' ? 'success.main' : 'error.main' }}
                        >
                          {spot.outcome === 'eligible' ? 'Kept' : 'Excluded'}
                        </Typography>
                        {spot.outcome !== 'eligible' && (
                          <Typography variant="caption" component="div" color="text.secondary">
                            {names[spot.outcome]}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {spot.rawPeak == null ? number(spot.signal) : `${number(spot.rawPeak)} DN`}
                      </Typography>
                    </ButtonBase>
                  ))}
                </Box>
                <Stack data-testid="spot-mesh-selected-result" spacing={1.5} sx={{ minWidth: 0 }}>
                  <Typography variant="body2" data-testid="spot-mesh-required-spacing">
                    Required minimum: {profile.minDistancePx} px
                    {resolution ? ` / ${number(profile.minDistancePx * resolution.umpp)} \u00b5m` : ''}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography variant="subtitle1" data-testid="spot-mesh-selected-probe">
                      Probe {selected}
                    </Typography>
                    <Typography variant="body2" sx={{ color: outcomeColor }} data-testid="spot-mesh-outcome">
                      {probe.outcome === 'eligible' ? 'Kept' : names[probe.outcome]}
                    </Typography>
                  </Stack>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Radius
                      </Typography>
                      <Typography variant="body1" data-testid="spot-mesh-radius">
                        {number(probe.radiusUm)} {'\u00b5m'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Spacing to {probe.strongerLabel ?? 'stronger probe'}
                      </Typography>
                      <Typography variant="body1" data-testid="spot-mesh-distance">
                        {Number.isFinite(probe.distanceToStronger) && resolution
                          ? `${number(probe.distanceToStronger)} px / ${number(
                              probe.distanceToStronger * resolution.umpp
                            )} \u00b5m`
                          : probe.outcome === 'signal'
                          ? 'Not evaluated (too faint)'
                          : resolution
                          ? 'Strongest probe'
                          : 'Resolution unavailable'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Contrast / required (0–1)
                      </Typography>
                      <Typography variant="body1" data-testid="spot-mesh-contrast">
                        {formatContrast(probe.signal, profile.minContrast)} / {profile.minContrast}
                        {model.referenceMax != null &&
                          ` (${number(probe.rawContrast!)} / ${number(model.rawMinimumContrast!)} DN)`}
                      </Typography>
                    </Box>
                  </Box>
                  {model.referenceMax != null && (
                    <Box data-testid="spot-mesh-intensity" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Background floor{' '}
                        <Box component="strong" color="text.primary">
                          {number(model.rawLowerBound!)} DN
                        </Box>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Upper clipping{' '}
                        <Box component="strong" color="text.primary">
                          {number(model.rawUpperBound!)} DN
                        </Box>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Probe peak{' '}
                        <Box component="strong" color="text.primary">
                          {number(probe.rawPeak!)} DN
                        </Box>
                      </Typography>
                    </Box>
                  )}
                  <Typography component="figcaption" variant="caption" color="text.secondary">
                    Illustrative probes, not MOG results.
                  </Typography>
                  <CollapsibleSection title="Technical details">
                    <Typography variant="caption" component="div" color="text.secondary">
                      Minimum contrast is the peak intensity minus the median background in a surrounding ring, after
                      clipping and rescaling to 0–1. A value of 0.1 requires 10% of that range. Peaks exactly at the
                      minimum pass this contrast check. This applies to unsaturated spots. Saturated regions use
                      separate handling.
                    </Typography>
                    {model.referenceMax != null && (
                      <Typography variant="caption" component="div" color="text.secondary">
                        Example minimum contrast: {profile.minContrast} × ({number(model.rawUpperBound!)} −{' '}
                        {number(model.rawLowerBound!)}) = {number(model.rawMinimumContrast!)} DN. Changing clipping also
                        changes this raw example threshold.
                      </Typography>
                    )}
                    {resolution && (
                      <Typography variant="caption" component="div" color="text.secondary">
                        Minimum spacing: {profile.minDistancePx} px ={' '}
                        {formatPeakDetectionSpacingDistanceLabel(resolution)}. Scale: {number(resolution.umpp)}{' '}
                        {'\u00b5m'}/px ({resolution.isCustom ? 'custom' : 'native'}).
                      </Typography>
                    )}
                    <Typography variant="caption" component="div" color="text.secondary">
                      Radius limits:{' '}
                      {profile.minRadiusUm == null ? 'no minimum' : `${number(profile.minRadiusUm)} \u00b5m`} /{' '}
                      {profile.maxRadiusUm == null ? 'no maximum' : `${number(profile.maxRadiusUm)} \u00b5m`}. Bounds
                      are inclusive.
                    </Typography>
                    {distanceGroups.length > 1 && (
                      <Typography variant="caption" component="div" color="text.secondary">
                        {distanceGroups.length} native resolution groups; example uses {number(resolution.umpp)}{' '}
                        {'\u00b5m'}
                        /px.
                      </Typography>
                    )}
                    {unknownResolutionCount > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Resolution unavailable for {unknownResolutionCount} selected slides.
                      </Typography>
                    )}
                    {bitDepths.some((depth) => depth == null) && (
                      <Typography variant="caption" color="text.secondary">
                        Bit depth unavailable for {bitDepths.filter((depth) => depth == null).length} selected slides.
                      </Typography>
                    )}
                  </CollapsibleSection>
                </Stack>
              </Box>
            </Stack>
          </>
        )}
      </Box>
    </Box>
  );
};
